import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useTenant } from './TenantContext'
import { createBasket, getBasket, addPackage, removePackage, getAuthLinks, loadTebexJs } from '../utils/tebex'

// Shared cart for the whole public site.
//
// The cart is LOCAL (held client-side + localStorage) — adding a package never
// calls Tebex, so it always works instantly with no sign-in. Tebex only gets
// involved at checkout: we create/authenticate a basket, push the local cart
// into it, then open Tebex.js. This avoids Tebex's "must login / invalid
// option" errors that fire on per-item adds.
//
// FiveM sign-in is a full redirect, so checkout resumes on return via ?checkout=1.

const BasketContext = createContext(null)
export const useBasket = () => useContext(BasketContext)

// Add a package to a cart list (immutably), bumping quantity if already present.
// Tebex flags VIP tiers, subscriptions, unbans, etc. with `disable_quantity`;
// it rejects quantity > 1 on those at checkout (failing the WHOLE order with an
// opaque "option is invalid" error), so we pin them to 1 and treat re-adds as
// no-ops. The flag is stored on the line so the cap survives without the pkg.
function mergeItem(list, pkg, qty = 1) {
  const i = list.findIndex((p) => p.id === pkg.id)
  const noQty = Boolean(pkg.disable_quantity)
  if (i >= 0) {
    if (list[i].noQty) return list // quantity-locked — re-adding does nothing
    const next = [...list]
    next[i] = { ...next[i], quantity: next[i].quantity + qty }
    return next
  }
  return [...list, {
    id: pkg.id,
    name: pkg.name,
    price: pkg.total_price ?? pkg.base_price ?? 0,
    currency: pkg.currency || 'USD',
    image: pkg.image || null,
    quantity: noQty ? 1 : qty,
    noQty,
    sub: Boolean(pkg.type === 'subscription'),
  }]
}

// Tebex bills one recurring agreement per basket, so a checkout can hold at most
// ONE subscription (one-time items alongside it are fine — that's how the
// gems+VIP bundles work). A second, DIFFERENT subscription is rejected at
// checkout, so we block it at add time. Returns the name of the conflicting
// subscription already in the cart, or null if adding `pkg` is allowed.
function conflictingSub(pkg, cart) {
  if (pkg?.type !== 'subscription') return null
  const other = cart.find((p) => p.sub && p.id !== pkg.id)
  return other ? other.name : null
}

export function BasketProvider({ children }) {
  const tenant = useTenant()
  const token = tenant?.tebex?.publicToken || ''
  const accent = tenant?.branding?.colors?.accent || '#3B82F6'
  const cartKey = `tebexCart:${token}`
  const basketKey = `tebexBasket:${token}`
  // Records which package ids we last pushed into the current Tebex basket, so a
  // reused basket can be reset to mirror the local cart before re-syncing.
  const pushedKey = `tebexPushed:${token}`
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey)) || [] } catch { return [] }
  })
  const [cartOpen, setCartOpen] = useState(false)
  const [addedItems, setAddedItems] = useState([]) // packages shown in the "added to basket" modal
  const [addedOpen, setAddedOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [fivemUsername, setFivemUsername] = useState(null)
  const [storeDiscord, setStoreDiscord] = useState(() => {
    try { return JSON.parse(localStorage.getItem('storeDiscord')) || null } catch { return null }
  })
  const resumed = useRef(false)
  const purchaseHandled = useRef(false)

  useEffect(() => {
    try { localStorage.setItem(cartKey, JSON.stringify(cart)) } catch { /* ignore */ }
  }, [cart, cartKey])

  // Reflect FiveM link status from an existing basket (e.g. after returning
  // from the CFX.RE auth redirect).
  useEffect(() => {
    if (!token) return
    const ident = localStorage.getItem(basketKey)
    if (!ident) return
    getBasket(token, ident).then((b) => setFivemUsername(b?.username || null)).catch(() => {})
  }, [token, basketKey])

  // ---- local cart ops -----------------------------------------------------

  // Quietly add to the cart WITHOUT opening/refreshing the modal — used by the
  // modal's own cross-sell "Add" buttons so clicking them doesn't reshuffle it.
  const addToCart = useCallback((pkg, qty = 1) => {
    if (!pkg?.id) return
    const clash = conflictingSub(pkg, cart)
    if (clash) {
      setActionError(`Your cart already has a subscription (${clash}). Only one subscription is allowed per order.`)
      return
    }
    setActionError(null)
    setCart((prev) => mergeItem(prev, pkg, qty))
  }, [cart])

  // Add a package to the local cart and pop the "added to basket" modal (with
  // cross-sell). The modal is global (mounted in PublicLayout), so this works
  // from anywhere — store cards, bundles, the home page, etc.
  const add = useCallback((pkg, qty = 1) => {
    if (!pkg?.id) return
    const clash = conflictingSub(pkg, cart)
    if (clash) {
      setActionError(`Your cart already has a subscription (${clash}). You can only buy one subscription per order — remove it first, or check out separately.`)
      setCartOpen(true) // surface the message + the existing subscription in context
      return
    }
    setActionError(null)
    setCart((prev) => mergeItem(prev, pkg, qty))
    setAddedItems([pkg])
    setAddedOpen(true)
  }, [cart])

  // Add several packages at once (e.g. a bundle), then show the modal.
  const addBundle = useCallback((pkgs) => {
    const valid = (pkgs || []).filter((p) => p?.id)
    if (!valid.length) return
    // Block if the merged result would hold more than one distinct subscription
    // (e.g. cart already has a VIP sub and the bundle bundles a different one).
    const subIds = new Set([
      ...cart.filter((p) => p.sub).map((p) => p.id),
      ...valid.filter((p) => p.type === 'subscription').map((p) => p.id),
    ])
    if (subIds.size > 1) {
      setActionError('You can only have one subscription per order. This bundle’s subscription conflicts with one already in your cart — check that out first.')
      setCartOpen(true)
      return
    }
    setActionError(null)
    setCart((prev) => valid.reduce((acc, p) => mergeItem(acc, p), prev))
    setAddedItems(valid)
    setAddedOpen(true)
  }, [cart])

  const remove = useCallback((id) => setCart((prev) => prev.filter((p) => p.id !== id)), [])

  const itemCount = cart.reduce((n, p) => n + p.quantity, 0)
  const subtotal = cart.reduce((n, p) => n + (p.price || 0) * p.quantity, 0)
  const currency = cart[0]?.currency || 'USD'

  // ---- checkout -----------------------------------------------------------

  // Wipe the local cart and Tebex basket bookkeeping after a completed purchase.
  // Called from two places because neither alone is reliable: the in-page
  // `payment:complete` event (fires only for the embedded lightbox, in this tab)
  // and the `?purchase=complete` return URL (the only signal we get when Tebex
  // redirects the tab — full-page/popup/mobile checkout, or a manual "return to
  // store"). Idempotent, so firing both is harmless.
  const clearPurchased = useCallback(() => {
    localStorage.removeItem(cartKey)
    localStorage.removeItem(basketKey)
    localStorage.removeItem(pushedKey)
    setCart([])
    setCartOpen(false)
  }, [cartKey, basketKey, pushedKey])

  // Push every local cart line into the Tebex basket, then open Tebex.js.
  const syncAndLaunch = useCallback(async (ident) => {
    // Every package requires the buyer's Discord id as a delivery option.
    const variableData = storeDiscord?.discordId ? { discord_id: storeDiscord.discordId } : undefined

    // This basket may be reused — kept across the FiveM sign-in redirect, or left
    // behind by a checkout the buyer opened then cancelled. In that case it still
    // holds the packages we pushed last time, which the buyer may since have
    // removed or re-quantified locally. The Headless API only lets us mirror the
    // cart by editing the basket (no clear endpoint, and basket packages carry no
    // reliable id to read back), so we strip exactly what we last pushed into THIS
    // basket — tracked below, so we only ever touch our own lines — then re-add
    // the current cart. Without this, removed items linger and re-added items
    // stack: cancel with "250 Gems", remove it, add "Strike Removal", check out,
    // and the basket would show both.
    let lastPushed = []
    try {
      const rec = JSON.parse(localStorage.getItem(pushedKey) || 'null')
      if (rec && rec.ident === ident) lastPushed = rec.ids || []
    } catch { /* ignore */ }
    for (const id of lastPushed) {
      await removePackage(ident, id).catch(() => {}) // a line already gone is fine
    }

    // Record what we're about to push BEFORE adding, so that if an addPackage
    // call fails partway (e.g. a disabled package), the lines that DID go in are
    // still tracked and get stripped on the next attempt — no orphaned items.
    try {
      localStorage.setItem(pushedKey, JSON.stringify({ ident, ids: cart.map((i) => i.id) }))
    } catch { /* ignore */ }
    for (const item of cart) {
      await addPackage(ident, item.id, item.quantity, variableData)
    }

    const Tebex = await loadTebexJs()
    Tebex.checkout.init({
      ident,
      theme: 'dark',
      colors: [{ name: 'primary', color: accent }, { name: 'secondary', color: accent }],
    })
    // The event is "payment:complete" (COLON) — Tebex.js never emits a dotted
    // "payment.complete", so that listener would silently never fire.
    Tebex.checkout.on('payment:complete', clearPurchased)
    Tebex.checkout.launch()
  }, [cart, accent, pushedKey, storeDiscord, clearPurchased])

  // Load the saved Tebex basket so it can be reused across the sign-in redirect
  // — but ONLY if it can still be paid into. Once a basket is paid, Tebex keeps
  // returning it (with complete: true) instead of 404ing, so a stale ident left
  // in localStorage would get reused and every addPackage/checkout call would
  // fail with "the basket with that identifier has already been paid for".
  // Dropping a completed basket forces a fresh one for the next purchase. This
  // is the real cleanup — the payment.complete event is best-effort and won't
  // fire if the buyer closes the modal, refreshes, or returns in a new session.
  const loadReusableBasket = useCallback(async () => {
    const ident = localStorage.getItem(basketKey)
    if (!ident) return null
    const basket = await getBasket(token, ident).catch(() => null)
    if (!basket || basket.complete) {
      localStorage.removeItem(basketKey)
      localStorage.removeItem(pushedKey)
      return null
    }
    return basket
  }, [token, basketKey, pushedKey])

  // `navigate` (optional, from the caller's router) lets us open the Tebex modal
  // on the /store page rather than wherever checkout was triggered. The FiveM
  // auth path already returns to /store?checkout=1, so passing it here makes the
  // already-linked path land on /store too — one consistent "back to store, then
  // pay" experience instead of the modal popping up on /signin.
  const checkout = useCallback(async (navigate) => {
    if (!cart.length || !token) return
    setBusy(true); setActionError(null)
    try {
      let basket = await loadReusableBasket()
      if (!basket) {
        basket = await createBasket(token, {
          completeUrl: `${origin}/store?purchase=complete`,
          cancelUrl: `${origin}/store`,
        })
        localStorage.setItem(basketKey, basket.ident)
      }
      // Not signed in → send to FiveM auth and resume checkout on return.
      if (!basket.username) {
        const providers = await getAuthLinks(token, basket.ident, `${origin}/store?checkout=1`)
        const p = providers.find((x) => /fivem/i.test(x.name)) || providers[0]
        if (p?.url) { window.location.href = p.url; return }
        setActionError('Sign-in is unavailable right now. Please try again shortly.')
        setBusy(false)
        return
      }
      // Land on /store before opening the modal (basket is loaded, no redirect
      // needed) so the payment screen always appears over the store, consistently.
      if (navigate) navigate('/store')
      await syncAndLaunch(basket.ident)
      setBusy(false)
    } catch (e) {
      setActionError(e.message || 'Checkout failed.')
      setBusy(false)
    }
  }, [cart, token, origin, basketKey, syncAndLaunch, loadReusableBasket])

  // Explicit FiveM sign-in step — ALWAYS sends the buyer to CFX.RE auth and
  // returns them to /signin. Auth only; no add or checkout here.
  const signInFivem = useCallback(async () => {
    setActionError(null)
    try {
      let basket = await loadReusableBasket()
      if (!basket) {
        basket = await createBasket(token, {
          completeUrl: `${origin}/store?purchase=complete`,
          cancelUrl: `${origin}/store`,
        })
        localStorage.setItem(basketKey, basket.ident)
      }
      const providers = await getAuthLinks(token, basket.ident, `${origin}/signin`)
      const p = providers.find((x) => /fivem/i.test(x.name)) || providers[0]
      if (p?.url) window.location.href = p.url
      else setActionError('FiveM sign-in is unavailable right now.')
    } catch (e) { setActionError(e.message) }
  }, [token, origin, basketKey, loadReusableBasket])

  // Unlink the connected FiveM account (e.g. linked the wrong character). The
  // FiveM username lives on the Tebex basket — there's no un-auth endpoint — so
  // we drop the basket entirely; the next signInFivem/checkout creates a fresh,
  // unlinked one and re-runs the Cfx.re auth. The LOCAL cart is untouched (it's
  // keyed separately and gets re-synced into the new basket at checkout).
  const unlinkFivem = useCallback(() => {
    try { localStorage.removeItem(basketKey) } catch { /* ignore */ }
    try { localStorage.removeItem(pushedKey) } catch { /* ignore */ }
    setFivemUsername(null)
  }, [basketKey, pushedKey])

  // Discord sign-in — gets the buyer's Discord id (required by every package as
  // variable_data.discord_id). Reuses the /auth/callback redirect with
  // state=store so AuthCallback routes it to the customer identify endpoint.
  const signInDiscord = useCallback(() => {
    const clientId = tenant?.discord?.clientId
    const path = tenant?.discord?.callbackPath || '/auth/callback'
    const redirectUri = encodeURIComponent(`${origin}${path}`)
    // prompt=consent forces Discord to show the authorize screen every time
    // (instead of silently re-using the already-authorized account), so a buyer
    // who linked the wrong Discord can switch accounts on the re-link.
    window.location.href =
      `https://discord.com/api/oauth2/authorize?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}&response_type=code&scope=identify&state=store&prompt=consent`
  }, [tenant, origin])

  // Clear the linked Discord identity (e.g. the buyer linked the wrong account).
  // Reflected in state immediately so the sign-in card flips back to "connect"
  // without a reload; the next signInDiscord re-prompts for the account.
  const unlinkDiscord = useCallback(() => {
    try { localStorage.removeItem('storeDiscord') } catch { /* ignore */ }
    try { sessionStorage.removeItem('storeDiscordJustLinked') } catch { /* ignore */ }
    setStoreDiscord(null)
  }, [])

  // After returning from FiveM auth (?checkout=1), finish the checkout.
  useEffect(() => {
    if (resumed.current || typeof window === 'undefined' || !token) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') !== '1') return
    resumed.current = true
    if (!localStorage.getItem(basketKey)) return
    setBusy(true)
    setCartOpen(true)
    loadReusableBasket()
      .then(async (b) => {
        window.history.replaceState({}, '', '/store')
        if (b?.username && cart.length) await syncAndLaunch(b.ident)
      })
      .catch((e) => setActionError(e.message))
      .finally(() => setBusy(false))
  }, [token, basketKey, cart, syncAndLaunch, loadReusableBasket])

  // Tebex sends the buyer back to completeUrl (/store?purchase=complete) after a
  // successful payment. This is the authoritative "paid" signal whenever the tab
  // was redirected — the in-page payment:complete event can't fire in that case.
  // Clear the cart here so a finished purchase never lingers in the basket.
  useEffect(() => {
    if (purchaseHandled.current || typeof window === 'undefined') return
    if (new URLSearchParams(window.location.search).get('purchase') !== 'complete') return
    purchaseHandled.current = true
    clearPurchased()
  }, [clearPurchased])

  const value = {
    enabled: Boolean(token),
    items: cart, itemCount, subtotal, currency,
    busy, actionError, setActionError,
    fivemUsername, signInFivem, unlinkFivem,
    discordUsername: storeDiscord?.username || null, signInDiscord, unlinkDiscord,
    cartOpen, openCart: () => setCartOpen(true), closeCart: () => setCartOpen(false),
    addedItems, addedOpen, closeAdded: () => setAddedOpen(false),
    add, addBundle, addToCart, remove, checkout,
  }
  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>
}
